/**
 * O detalhe de uma OS: a ficha inteira, o que já aconteceu e o que falta.
 *
 * Responde às perguntas que o escritório faz o dia todo — onde está, por que
 * atrasou, quanto levou, quem estava na máquina — sem precisar telefonar para
 * o produção.
 *
 * A linha do tempo mostra **inclusive o que foi estornado**, riscado. Sumir
 * com o erro esconderia a única pista de por que o número mudou.
 */

import { formato, urlTemporaria, type EventoProducao } from '@pcp/dominio';
import type { DetalheOs } from '@pcp/dados';
import {
  abrirMenu,
  aviso,
  botao,
  h,
  marcasDaOs,
  notificar,
  render,
  semaforo,
  sf,
  situacaoDaEtapa,
  situacaoDaOs,
  tempos,
  vazio,
  type Filho,
} from '@pcp/ui';
import type { Aplicacao } from '../estado.js';
import { perguntar, confirmar } from '../dialogos.js';

export async function telaDetalhe(
  app: Aplicacao,
  destino: HTMLElement,
  numero: number,
): Promise<void> {
  const d = await app.repo.detalhe(numero);
  if (!d) {
    render(
      destino,
      vazio(
        'questionmark-circle',
        `OS ${numero} não está no sistema`,
        'Ou o número está errado, ou o PDF dela ainda não foi importado.',
        botao('Ver a carteira', () => app.roteador.ir('/carteira'), { estilo: 'primary' }),
      ),
    );
    return;
  }

  const cadastros = await app.repo.carregarCadastros();
  const nomeDe = (id: string | null): string =>
    id ? (cadastros.pessoas.find((p) => p.id === id)?.nome ?? '—') : '—';
  const motivoDe = (id: string | null): string =>
    id ? (cadastros.motivos.find((m) => m.id === id)?.descricao ?? id) : '';

  render(destino, [
    h('.vstack.gap-4', [
      cabecalho(app, d),
      d.os.canceladaEm
        ? aviso('erro', [
            h('b', 'OS cancelada. '),
            d.os.motivoCancelamento ?? 'Sem motivo registrado.',
            ` (${formato.dataHoraCurta(d.os.canceladaEm)})`,
          ])
        : null,
      d.temEtapaMarcada
        ? aviso(
            'aviso',
            'A OS foi alterada depois que a produção começou. As etapas marcadas com o triângulo precisam de conferência.',
          )
        : null,
      d.temEtapaAClassificar
        ? aviso(
            'aviso',
            'Há etapa sem estação definida. Ela não aparece em fila nenhuma até ser classificada.',
            botao('Ir para a triagem', () => app.roteador.ir('/triagem'), { pequeno: true }),
          )
        : null,
      d.os.observacoesGerais.length > 0
        ? aviso('info', [
            h('b', 'Observações da OS: '),
            d.os.observacoesGerais.join(' · '),
          ])
        : null,

      h('.pcp-duas-colunas', [
        h('.vstack.gap-4', [
          blocoEtapas(app, d, nomeDe),
          blocoLinhaDoTempo(d, nomeDe, motivoDe),
        ]),
        h('.vstack.gap-4', [
          blocoFicha(d),
          blocoPlanos(d),
          blocoMateriais(d),
          blocoVersoes(d),
          blocoArquivos(app, d),
        ]),
      ]),
    ]),
  ]);
}

/* ------------------------------------------------------------------ */

function cabecalho(app: Aplicacao, d: DetalheOs): HTMLElement {
  const situacao = situacaoDaOs(d);

  return h('.cartao', { style: { padding: 'var(--sp-4)' } }, [
    h('.hstack.gap-3.wrap', [
      h('button.mac-tb-btn.icon', {
        type: 'button',
        'data-tooltip': 'Voltar',
        onclick: () => app.roteador.ir('/carteira'),
      }, sf('chevron-left')),
      h('.vstack', { style: { gap: '2px', flex: '1', minWidth: '260px' } }, [
        h('.hstack.gap-2', [
          h('span.pcp-mono.t-title2', { style: { color: 'var(--label-2)' } }, `OS ${d.os.numero}`),
          h('span.t-title2', d.os.clienteCurto),
          semaforo(situacao, { grande: true }),
          ...marcasDaOs(d),
        ]),
        h('.t-body.t-secondary', `${formato.numero(d.os.quantidade)} · ${d.os.produto}`),
      ]),
      h('.vstack', { style: { gap: '0', textAlign: 'right' } }, [
        h('.t-subhead.t-secondary', 'Entrega prometida'),
        h('.t-title3', formato.dataHoraLonga(d.os.previsaoEntregaEm)),
        h(
          '.t-subhead',
          { style: { color: d.atrasada ? 'var(--sys-red)' : 'var(--label-2)' } },
          d.atrasada
            ? `Atrasada ${formato.distanciaNoTempo(d.os.previsaoEntregaEm)}`
            : formato.distanciaNoTempo(d.os.previsaoEntregaEm),
        ),
      ]),
    ]),

    h('hr.mac-separator', { style: { margin: 'var(--sp-3) 0' } }),

    h('.hstack.gap-3.wrap', [
      indicador('Etapas', `${d.estado.etapasConcluidas} de ${d.estado.etapasTotal}`),
      indicador('Tempo previsto', formato.hhmm(d.estado.tempoPrevistoTotalSeg)),
      indicador('Tempo medido', formato.hhmm(d.estado.tempoRealTotalSeg)),
      indicador('Falta', formato.hhmm(d.estado.tempoPrevistoRestanteSeg)),
      d.emRisco ? h('.hstack.gap-1', [semaforo('risco')]) : null,
      h('.spacer'),
      ...acoes(app, d),
    ]),
  ]);
}

function indicador(rotulo: string, valor: string): HTMLElement {
  return h('.vstack', { style: { gap: '0' } }, [
    h('.t-footnote.t-secondary', rotulo),
    h('.t-body-emph.pcp-num', valor),
  ]);
}

function acoes(app: Aplicacao, d: DetalheOs): HTMLElement[] {
  const recarregar = (): void => app.redesenhar();
  const lista: HTMLElement[] = [];

  lista.push(
    botao(
      d.os.prioridadeEm ? 'Tirar prioridade' : 'Dar prioridade',
      async () => {
        await app.repo.definirPrioridade(d.os.numero, !d.os.prioridadeEm);
        notificar({
          titulo: d.os.prioridadeEm ? 'Prioridade removida' : 'OS priorizada',
          corpo: `OS ${d.os.numero} ${d.os.prioridadeEm ? 'voltou para a ordem normal' : 'foi para o topo das filas'}.`,
          icone: 'flag-fill',
        });
        recarregar();
      },
      { icone: 'flag', pequeno: true },
    ),
  );

  if (d.estado.status === 'pronta') {
    lista.push(
      botao(
        'Registrar saída',
        async () => {
          const quem = await perguntar({
            titulo: `Saída da OS ${d.os.numero}`,
            mensagem:
              d.os.entregaTipo === 'RETIRADA'
                ? 'Quem retirou o material?'
                : 'Quem levou o material?',
            rotuloCampo: 'Nome',
            confirmar: 'Registrar',
          });
          if (quem === null) return;
          await app.repo.registrarExpedicao({
            osNumero: d.os.numero,
            tipo: d.os.entregaTipo === 'RETIRADA' ? 'retirada' : 'expedida',
            quemLevou: quem || null,
            transportadora: d.os.transportadora,
            pessoaId: app.sessao.pessoaId,
          });
          notificar({ titulo: `OS ${d.os.numero} concluída`, icone: 'checkmark-circle' });
          recarregar();
        },
        { icone: 'tray', estilo: 'primary', pequeno: true },
      ),
    );
  }

  if (!d.os.canceladaEm) {
    lista.push(
      botao(
        'Cancelar OS',
        async () => {
          const motivo = await perguntar({
            titulo: `Cancelar a OS ${d.os.numero}?`,
            mensagem:
              'O sistema da gráfica não avisa cancelamento — por isso ele é registrado aqui, à mão. A OS sai das filas do produção.',
            rotuloCampo: 'Motivo',
            confirmar: 'Cancelar OS',
            destrutivo: true,
            obrigatorio: true,
          });
          if (!motivo) return;
          await app.repo.cancelarOs(d.os.numero, motivo, app.sessao.pessoaId);
          notificar({ titulo: `OS ${d.os.numero} cancelada`, icone: 'xmark-circle-fill' });
          recarregar();
        },
        { icone: 'xmark', estilo: 'destructive', pequeno: true },
      ),
    );
  } else {
    lista.push(
      botao(
        'Reabrir',
        async () => {
          if (!(await confirmar(`Reabrir a OS ${d.os.numero}?`, 'Ela volta para as filas do produção.'))) return;
          await app.repo.reabrirOs(d.os.numero);
          recarregar();
        },
        { icone: 'arrow-clockwise', pequeno: true },
      ),
    );
  }

  return lista;
}

/* ------------------------------------------------------------------ */

function blocoEtapas(
  app: Aplicacao,
  d: DetalheOs,
  nomeDe: (id: string | null) => string,
): HTMLElement {
  const operacao = d.etapas.filter((e) => e.etapa.tipoLinha === 'operacao');
  const custo = d.etapas.filter((e) => e.etapa.tipoLinha === 'custo');

  return h('.cartao', [
    h('.hstack', { style: { padding: '0 0 var(--sp-2)' } }, [
      h('.bloco-t', 'Etapas'),
      h('.spacer'),
      botao('Adicionar etapa', () => adicionarEtapa(app, d), { icone: 'plus', pequeno: true }),
    ]),

    h(
      '.vstack',
      { style: { gap: '2px' } },
      operacao.map((e, i) => linhaEtapa(app, e, i + 1, nomeDe)),
    ),

    custo.length > 0
      ? h('.vstack.gap-1', { style: { marginTop: 'var(--sp-3)' } }, [
          h('.rot', 'Lançamentos de custo — não são tarefa de ninguém'),
          ...custo.map((e) =>
            h('.pcp-etapa-linha.custo', [
              h('.pcp-etapa-ordem', sf('tag', 'sf-xs')),
              h('.vstack', { style: { gap: '0' } }, [
                h('.t-callout', e.etapa.nomeOs),
                h('.t-subhead.t-secondary', 'Custo por clique da impressora digital'),
              ]),
              h('span.t-subhead.t-tertiary', e.etapa.plano ? `Plano ${e.etapa.plano}` : ''),
            ]),
          ),
        ])
      : null,
  ]);
}

function linhaEtapa(
  app: Aplicacao,
  e: DetalheOs['etapas'][number],
  ordem: number,
  nomeDe: (id: string | null) => string,
): HTMLElement {
  const s = e.estado.status;
  const classe =
    s === 'concluida' || s === 'pulada'
      ? 'feita'
      : s === 'em_execucao'
        ? 'correndo'
        : s === 'pausada'
          ? 'parada'
          : '';

  const detalhes: string[] = [];
  if (e.estacao) detalhes.push(e.estacao.nome);
  if (e.etapa.plano) detalhes.push(`Plano ${e.etapa.plano}`);
  if (e.etapa.impressoesPrevistas !== null) {
    detalhes.push(
      `${formato.numero(e.etapa.impressoesPrevistas)}${e.etapa.unidade ? ` ${e.etapa.unidade}` : ''}`,
    );
  }
  if (e.etapa.observacoesOs) detalhes.push(e.etapa.observacoesOs);
  if (e.estado.operadores.length > 0) {
    detalhes.push(e.estado.operadores.map(nomeDe).join(', '));
  }

  return h(`.pcp-etapa-linha${classe ? `.${classe}` : ''}`, [
    h('.pcp-etapa-ordem', String(ordem)),
    h('.vstack', { style: { gap: '1px', minWidth: '0' } }, [
      h('.hstack.gap-2', [
        h('span.t-body-emph', e.etapa.nomeOs),
        e.etapa.marcadaVerificar
          ? h('span.pcp-marca.alterada', { title: 'A OS mudou depois que esta etapa começou' }, [
              sf('exclamationmark-triangle'),
              'confira',
            ])
          : null,
        e.etapa.aClassificar
          ? h('span.pcp-marca.triagem', { title: 'Sem estação definida' }, [sf('questionmark-circle'), 'sem estação'])
          : null,
        e.etapa.origem === 'retrabalho'
          ? h('span.pcp-marca', [sf('arrow-clockwise'), 'retrabalho'])
          : null,
        e.etapa.origem === 'manual'
          ? h('span.pcp-marca', [sf('plus'), 'não prevista na OS'])
          : null,
        ...e.estado.marcas.map((m) =>
          h('span.pcp-marca.alterada', { title: explicarMarca(m) }, [sf('exclamationmark-triangle'), rotuloMarca(m)]),
        ),
      ]),
      h('.t-subhead.t-secondary.t-truncate', detalhes.join(' · ')),
    ]),
    h('.hstack.gap-3', [
      semaforo(situacaoDaEtapa(s)),
      tempos(e.etapa.tempoPrevistoSeg, e.estado.tempoRealSeg),
      e.estado.qtdBoa !== null
        ? h('.vstack', { style: { gap: '0', textAlign: 'right', minWidth: '76px' } }, [
            h('.t-body-emph.pcp-num', formato.numero(e.estado.qtdBoa)),
            e.estado.qtdRefugo
              ? h('.t-subhead', { style: { color: 'var(--sys-red)' } }, `${formato.numero(e.estado.qtdRefugo)} refugo`)
              : h('.t-subhead.t-tertiary', 'boas'),
          ])
        : null,
      menuDaEtapa(app, e),
    ]),
  ]);
}

function menuDaEtapa(app: Aplicacao, e: DetalheOs['etapas'][number]): HTMLElement {
  return h(
    'button.mac-tb-btn.icon',
    {
      type: 'button',
      'data-tooltip': 'Ações da etapa',
      onclick: (ev: MouseEvent) => {
        ev.stopPropagation();
        const concluida = e.estado.status === 'concluida';
        abrirMenu(
          ev.currentTarget as HTMLElement,
          [
            { label: 'Pular etapa…', value: 'pular', icon: 'forward-fill', disabled: concluida },
            { label: 'Abrir retrabalho…', value: 'retrabalho', icon: 'arrow-clockwise', disabled: !concluida },
            '-',
            { label: 'Limpar marca de conferência', value: 'limpar', icon: 'checkmark', disabled: !e.etapa.marcadaVerificar },
          ],
          async (valor) => {
            if (valor === 'pular') {
              const motivo = await perguntar({
                titulo: `Pular "${e.etapa.nomeOs}"?`,
                mensagem: 'A etapa conta como terminada e a OS segue para a próxima. Fica registrado quem pulou e por quê.',
                rotuloCampo: 'Motivo',
                confirmar: 'Pular etapa',
                obrigatorio: true,
              });
              if (!motivo) return;
              await app.repo.registrarEvento({
                osEtapaId: e.etapa.id,
                tipo: 'pulo',
                pessoaId: app.sessao.pessoaId,
                texto: motivo,
                origem: 'web',
              });
              app.redesenhar();
            }
            if (valor === 'retrabalho') {
              const motivo = await perguntar({
                titulo: 'Abrir retrabalho',
                mensagem: `Cria uma nova etapa "${e.etapa.nomeOs} (retrabalho)" com tempo contado à parte.`,
                rotuloCampo: 'Motivo',
                confirmar: 'Abrir',
                obrigatorio: true,
              });
              if (!motivo) return;
              await app.repo.abrirRetrabalho(e.etapa.id, motivo);
              app.redesenhar();
            }
            if (valor === 'limpar') {
              await app.repo.limparMarcaDeVerificacao(e.etapa.id);
              app.redesenhar();
            }
          },
        );
      },
    },
    sf('ellipsis'),
  );
}

function rotuloMarca(m: string): string {
  if (m === 'quantidade_acima_do_previsto') return 'quantidade';
  if (m === 'conclusao_duplicada') return 'duplicidade';
  if (m === 'inicio_concorrente') return 'dois operadores';
  if (m === 'encerrada_automaticamente') return 'fim de turno';
  return m;
}

function explicarMarca(m: string): string {
  if (m === 'quantidade_acima_do_previsto') return 'A quantidade apontada passou da prevista mais a tolerância.';
  if (m === 'conclusao_duplicada') return 'A etapa foi concluída mais de uma vez.';
  if (m === 'inicio_concorrente') return 'Dois operadores iniciaram a mesma etapa.';
  if (m === 'encerrada_automaticamente') return 'A etapa foi encerrada pelo fim do expediente, não por alguém.';
  return m;
}

async function adicionarEtapa(app: Aplicacao, d: DetalheOs): Promise<void> {
  const cadastros = await app.repo.carregarCadastros();
  const nome = await perguntar({
    titulo: 'Adicionar etapa não prevista na OS',
    mensagem:
      'Ela nasce marcada como "não prevista", e vira relatório para quem faz o orçamento ajustar a ficha no sistema da gráfica.',
    rotuloCampo: 'Nome da etapa',
    confirmar: 'Continuar',
    obrigatorio: true,
  });
  if (!nome) return;

  const opcoes = cadastros.estacoes
    .filter((e) => e.ativa)
    .map((e) => ({ valor: e.id, rotulo: `${e.nome} (${cadastros.setores.find((s) => s.id === e.setorId)?.nome})` }));

  const estacaoId = await perguntar({
    titulo: 'Em que estação?',
    mensagem: `A etapa "${nome}" vai aparecer na fila desta estação.`,
    rotuloCampo: 'Estação',
    opcoes,
    confirmar: 'Adicionar',
    obrigatorio: true,
  });
  if (!estacaoId) return;

  const estacao = cadastros.estacoes.find((e) => e.id === estacaoId);
  const setor = cadastros.setores.find((s) => s.id === estacao?.setorId);
  // A seção sai do PAPEL do setor, não do nome: outra empresa chama os setores
  // de outro jeito. `outro` cai em acabamento (a seção mais comum para etapa
  // avulsa); o encarregado ajusta depois se precisar.
  const secao =
    setor?.papel === 'pre_impressao'
      ? ('pre_impressao' as const)
      : setor?.papel === 'impressao'
        ? ('impressao' as const)
        : ('acabamento' as const);

  await app.repo.adicionarEtapa({
    osNumero: d.os.numero,
    nome,
    secao,
    estacaoId,
    tempoPrevistoSeg: null,
  });
  notificar({ titulo: 'Etapa adicionada', corpo: nome, icone: 'plus-circle' });
  app.redesenhar();
}

/* ------------------------------------------------------------------ */

function blocoLinhaDoTempo(
  d: DetalheOs,
  nomeDe: (id: string | null) => string,
  motivoDe: (id: string | null) => string,
): HTMLElement {
  if (d.eventos.length === 0) {
    return h('.cartao', [
      h('.bloco-t', 'Linha do tempo'),
      vazio(
        'clock',
        'Nada apontado ainda',
        'Assim que alguém tocar em "Iniciar" no tablet do setor, o que acontecer com esta OS aparece aqui.',
      ),
    ]);
  }

  const estornados = new Set(
    d.eventos.filter((e) => e.tipo === 'estorno' && e.estornaEventoId).map((e) => e.estornaEventoId),
  );
  const nomeEtapa = new Map(d.etapas.map((e) => [e.etapa.id, e.etapa.nomeOs]));

  return h('.cartao', [
    h('.bloco-t', 'Linha do tempo'),
    h(
      'ul.pcp-linha-tempo',
      [...d.eventos]
        .reverse()
        .map((ev) => itemDaLinhaDoTempo(ev, estornados.has(ev.id), nomeEtapa, nomeDe, motivoDe)),
    ),
  ]);
}

function itemDaLinhaDoTempo(
  ev: EventoProducao,
  estornado: boolean,
  nomeEtapa: Map<string, string>,
  nomeDe: (id: string | null) => string,
  motivoDe: (id: string | null) => string,
): HTMLElement {
  const cor = corDoEvento(ev.tipo);
  const etapa = nomeEtapa.get(ev.osEtapaId) ?? 'etapa removida';

  const texto: Filho = (() => {
    switch (ev.tipo) {
      case 'inicio':
        return [h('b', nomeDe(ev.pessoaId)), ' iniciou ', h('b', etapa)];
      case 'pausa': {
        const m = motivoDe(ev.motivoId);
        return [
          h('b', nomeDe(ev.pessoaId)),
          ' pausou ',
          h('b', etapa),
          m ? ` — ${m}` : '',
          ev.texto && ev.texto !== '__encerrada_automaticamente__' ? ` (${ev.texto})` : '',
        ];
      }
      case 'retomada':
        return [h('b', nomeDe(ev.pessoaId)), ' retomou ', h('b', etapa)];
      case 'conclusao':
        return [
          h('b', nomeDe(ev.pessoaId)),
          ' concluiu ',
          h('b', etapa),
          ev.qtdBoa !== null
            ? ` — ${formato.numero(ev.qtdBoa)} boas${ev.qtdRefugo ? `, ${formato.numero(ev.qtdRefugo)} refugo` : ''}`
            : '',
          ev.texto ? ` (${ev.texto})` : '',
        ];
      case 'pulo':
        return [h('b', nomeDe(ev.pessoaId)), ' pulou ', h('b', etapa), ev.texto ? ` — ${ev.texto}` : ''];
      case 'estorno':
        return [h('b', nomeDe(ev.pessoaId)), ' estornou um apontamento', ev.texto ? ` — ${ev.texto}` : ''];
      default:
        return ev.tipo;
    }
  })();

  return h(`li.pcp-evento${estornado ? '.estornado' : ''}`, { style: { '--pcp-cor': cor } }, [
    h('.quando', { title: formato.dataHoraLonga(ev.ocorridoEm) }, formato.dataHoraCurta(ev.ocorridoEm)),
    h('.trilho', h('.bolinha')),
    h('.texto', [
      texto,
      ev.origem === 'sistema' ? h('span.t-subhead.t-tertiary', ' · pelo sistema') : null,
      ev.desvioRelogioSeg !== null && Math.abs(ev.desvioRelogioSeg) > 300
        ? h('span.t-subhead', { style: { color: 'var(--sys-orange)' }, title: 'O relógio do aparelho está longe do relógio do servidor' }, ' · relógio fora')
        : null,
    ]),
  ]);
}

function corDoEvento(tipo: EventoProducao['tipo']): string {
  if (tipo === 'inicio' || tipo === 'retomada') return 'var(--pcp-producao)';
  if (tipo === 'pausa') return 'var(--pcp-pausada)';
  if (tipo === 'conclusao') return 'var(--pcp-pronta)';
  if (tipo === 'pulo') return 'var(--pcp-concluida)';
  return 'var(--sys-red)';
}

/* ------------------------------------------------------------------ */

function blocoFicha(d: DetalheOs): HTMLElement {
  const linhas: Array<[string, Filho]> = [
    ['Emissão', `${formato.dataHoraCurta(d.os.emitidaEm)} por ${d.os.emitidaPor}`],
    d.os.alteradaEm
      ? ['Alteração', `${formato.dataHoraCurta(d.os.alteradaEm)} por ${d.os.alteradaPor ?? '—'}`]
      : null,
    d.os.orcamento
      ? ['Orçamento', `${d.os.orcamento.numero}.${d.os.orcamento.item} · ${d.os.orcamento.vendedor}`]
      : null,
    ['Cliente', d.cliente ? `${d.cliente.codigoErp} — ${d.cliente.nome}` : d.os.clienteCurto],
    d.os.contato?.nome || d.os.contato?.telefone
      ? ['Contato', [d.os.contato.nome, d.os.contato.telefone, d.os.contato.email].filter(Boolean).join(' · ')]
      : null,
    ['Entrega', d.os.entregaTipo ?? '—'],
    ['Nota', d.os.notaTipo ?? '—'],
    d.os.transportadora ? ['Transportadora', d.os.transportadora] : null,
    d.os.arquivar ? ['Arquivar OS', d.os.arquivar] : null,
    d.os.acabamentoVinil ? ['Acabamento do vinil', d.os.acabamentoVinil] : null,
    d.os.concluidaNoErp ? ['No sistema da gráfica', 'Marcada como concluída'] : null,
  ].filter(Boolean) as Array<[string, Filho]>;

  return h('.cartao', [
    h('.bloco-t', 'Ficha'),
    h(
      'dl.pcp-ficha',
      linhas.map(([rotulo, valor]) => h('div', [h('dt', rotulo), h('dd', valor)])),
    ),
  ]);
}

function blocoPlanos(d: DetalheOs): HTMLElement | null {
  if (d.planos.length === 0) return null;
  return h('.cartao', [
    h('.bloco-t', d.planos.length > 1 ? 'Planos' : 'Plano'),
    h(
      '.vstack.gap-3',
      d.planos.map((p) => {
        const campos: Array<[string, string | null]> = [
          ['Cores', p.tipo === 'pags_cores_lams' ? `${p.paginas} pág · ${p.cores} · ${p.laminas} lâminas` : p.cores],
          ['Formato final', p.formatoFinal ? `${p.formatoFinal} ${p.formatoFinalUnidade ?? ''}${p.pecasPorFolha ? ` · ${p.pecasPorFolha} por folha` : ''}` : null],
          ['Material', p.materialCodigo ? `${p.materialCodigo}${p.materialFormato ? ` (${p.materialFormato})` : ''}` : null],
          ['Montagem', p.montagem ? `${p.montagem}${p.tiraRetira ? ' · tira-retira' : ''}` : null],
          ['Impressora', p.impressoraCodigo],
          ['Formato de impressão', p.formatoImpressao],
          ['Refile / sangria', p.refileSangria],
          ['Faca', p.faca],
          ['Lombada', p.lombada],
          ['Solda', p.solda ? 'sim' : null],
        ];
        return h('.vstack.gap-2', [
          d.planos.length > 1 ? h('.rot', `Plano ${p.numero}`) : null,
          h(
            'dl.pcp-ficha',
            campos
              .filter(([, v]) => v)
              .map(([r, v]) => h('div', [h('dt', r), h('dd', v as string)])),
          ),
        ]);
      }),
    ),
  ]);
}

function blocoMateriais(d: DetalheOs): HTMLElement | null {
  if (d.materiais.length === 0) return null;
  return h('.cartao', [
    h('.bloco-t', 'Materiais'),
    h('table.tab', [
      h('tbody',
        d.materiais.map((m) =>
          h('tr', [
            h('td', [
              m.codigo ? h('span.t-subhead.t-tertiary.pcp-mono', `${m.codigo} `) : null,
              m.nome,
              m.plano ? h('span.t-subhead.t-secondary', ` · plano ${m.plano}`) : null,
            ]),
            h('td.num', formato.quantidade(m.quantidade, m.unidade)),
            h('td.num.t-subhead.t-secondary', m.quebraPct !== null ? `${formato.numero(m.quebraPct, 2)}%` : ''),
          ]),
        ),
      ),
    ]),
    h('.t-footnote.t-tertiary', { style: { paddingTop: '6px' } },
      'A lista vem da OS. O PCP não controla saldo de estoque.'),
  ]);
}

function blocoVersoes(d: DetalheOs): HTMLElement | null {
  if (d.versoes.length <= 1) return null;
  return h('.cartao', [
    h('.bloco-t', `Versões da OS (${d.versoes.length})`),
    h(
      '.vstack.gap-2',
      d.versoes.map((v) =>
        h('.vstack', { style: { gap: '2px' } }, [
          h('.hstack.gap-2', [
            h('span.t-body-emph', `v${v.versao}`),
            h('span.t-subhead.t-secondary', formato.dataHoraCurta(v.aplicadaEm)),
            v.versao === d.os.versaoAtual ? h('span.selo.s-cinza', 'atual') : null,
          ]),
          v.diffResumo.length > 0
            ? h('ul', { style: { margin: '0', paddingLeft: '18px' } },
                v.diffResumo.map((linha) => h('li.t-callout.t-secondary', linha)))
            : h('.t-callout.t-tertiary', 'Primeira versão.'),
        ]),
      ),
    ),
  ]);
}

function blocoArquivos(app: Aplicacao, d: DetalheOs): HTMLElement | null {
  if (!d.os.pdfRef && !d.os.imagemFacaRef) return null;

  const area = h('.vstack.gap-2');
  const cartao = h('.cartao', [h('.bloco-t', 'Arquivos da OS'), area]);

  void (async () => {
    const filhos: Filho[] = [];

    if (d.os.imagemFacaRef) {
      const arq = await app.repo.arquivo(d.os.imagemFacaRef);
      if (arq) {
        const url = urlTemporaria(arq.dados, arq.tipo);
        filhos.push(
          h('.vstack.gap-1', [
            h('.rot', 'Desenho da faca / imposição'),
            h('img', {
              src: url,
              alt: 'Desenho da faca da OS',
              style: { width: '100%', borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-1)', background: '#fff' },
            }),
          ]),
        );
      }
    }

    if (d.os.pdfRef) {
      filhos.push(
        botao(
          'Abrir o PDF original',
          async () => {
            const arq = await app.repo.arquivo(d.os.pdfRef as string);
            if (!arq) return;
            const url = urlTemporaria(arq.dados, 'application/pdf');
            window.open(url, '_blank', 'noopener');
          },
          { icone: 'doc-text' },
        ),
      );
    }

    render(area, filhos);
  })();

  return cartao;
}
